import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { HeartHandshake, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Chaplain-facing accept-invite page. Same backend endpoint
 * (POST /auth/accept-invite) as the Management Center flow — no duplicate
 * invitation infrastructure. On success, routes to /chaplain-portal/login.
 */
export default function ChaplainAcceptInvitePage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const uid = params.get("uid") || "";
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!uid || !token) { toast.error("Link is missing token."); return; }
    if (pw !== pw2) { toast.error("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, { uid, token, password: pw });
      setDone(true);
      toast.success("Account activated. Please sign in.");
      setTimeout(() => nav("/chaplain-portal/login"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4 py-12"
      data-testid="chaplain-accept-invite-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#4A6FA5] text-white mb-5">
            <HeartHandshake className="w-8 h-8" strokeWidth={1.75} />
          </div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">CCCHAPS Chaplain Portal</h1>
          <p className="mt-2 text-sm text-slate-500">Accept your invitation to activate your chaplain account.</p>
        </div>

        <div className="w-full bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
          {done ? (
            <p className="text-sm text-slate-700">You're all set. Redirecting to Chaplain Login…</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="chaplain-accept-invite-form">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1.5">New password</span>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="chaplain-accept-password"
                  minLength={10}
                  required
                  className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</span>
                <input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="chaplain-accept-password-confirm"
                  minLength={10}
                  required
                  className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent"
                />
              </label>
              <p className="text-xs text-slate-500">
                Minimum 10 characters, including at least one letter and one digit. Cannot match your email.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="chaplain-accept-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors disabled:opacity-60"
              >
                <KeyRound className="w-4 h-4" /> {submitting ? "Activating…" : "Activate account"}
              </button>
              <p className="text-center text-xs text-slate-500 pt-2">
                <Link to="/chaplain-portal/login" className="text-[#4A6FA5] hover:underline">Back to Chaplain Login</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
